import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import MiniChart, { KlineData, SignalData } from '../../components/MiniChart/MiniChart';
import ChartModal from '../../components/ChartModal/ChartModal';
import './BotDetail.css';

interface Bot {
  id: string;
  strategy_type: string;
  exchange: string;
  symbol: string;
  scan_mode: boolean;
  status: string;
  config: any;
  error_message?: string;
}

interface ScoreBreakdown {
  total: number;
  bb_width: number;
  volume_lull: number;
  atr_comp: number;
  oi_growth: number;
  funding: number;
  range_high: number;
  range_low: number;
}

interface CompletedSignalData {
  symbol: string;
  direction: string;
  entry_price: number;
  exit_price: number;
  stop_loss: number;
  take_profit: number;
  result: string;
  virtual_pnl_pct: number;
  squeeze_score: number;
  strategy: string;
  signal_time: number;
  exit_time: number;
  duration_sec: number;
}

interface SessionStats {
  total_signals: number;
  wins: number;
  losses: number;
  timeouts: number;
  expired: number;
  win_rate: number;
  avg_pnl_pct: number;
  session_start_unix: number;
  session_duration_sec: number;
}

interface ChartDataResponse {
  charts: SymbolChartData[];
  completed_signals?: CompletedSignalData[];
  session_stats?: SessionStats;
}

interface SymbolChartData {
  symbol: string;
  last_price: number;
  klines: KlineData[];
  signal?: SignalData | null;
  phase?: string;
  indicators?: Record<string, any>;
  turnover_24h?: number;
  volume_rank?: number;
  squeeze_score?: number;
  score_breakdown?: ScoreBreakdown;
  funding_rate?: number;
}

const BotDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [bot, setBot] = useState<Bot | null>(null);
  const [chartData, setChartData] = useState<SymbolChartData[]>([]);
  const [completedSignals, setCompletedSignals] = useState<CompletedSignalData[]>([]);
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolChartData | null>(null);
  const [showConfig, setShowConfig] = useState(false);

  const fetchBot = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl(`/api/bots/${id}`), { credentials: 'include' });
      if (res.ok) {
        setBot(await res.json());
      }
    } catch (err: any) {
      setError(err.message);
    }
  }, [id]);

  const fetchChartData = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl(`/api/bots/${id}/chart-data`), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Support both old format (has .symbols) and new format (has .charts)
        if (data.charts) {
          setChartData(data.charts);
          setCompletedSignals(data.completed_signals || []);
          setSessionStats(data.session_stats || null);
        } else {
          setChartData(data.symbols || []);
        }
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchBot();
      await fetchChartData();
      setLoading(false);
    };
    init();
  }, [fetchBot, fetchChartData]);

  // Auto-refresh chart data every 2s for live feel
  useEffect(() => {
    if (!bot || (bot.status !== 'running' && bot.status !== 'paused')) return;
    const interval = setInterval(fetchChartData, 2000);
    return () => clearInterval(interval);
  }, [bot, fetchChartData]);

  const getStrategyName = (t: string) => {
    const names: Record<string, string> = {
      volume_breakout: 'Volume Breakout',
      volume_breakout_v2: 'Volume Breakout V2',
      ob_imbalance: 'OB Imbalance',
      momentum_ignition: 'Momentum Ignition',
      funding_rate_scalp: 'Funding Rate Scalp',
    };
    return names[t] || t;
  };

  const formatDuration = (sec: number): string => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const getStatusClass = (s: string) => {
    switch (s) {
      case 'running': return 'status-running';
      case 'paused': return 'status-paused';
      case 'error': return 'status-error';
      default: return 'status-stopped';
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 pt-20 pb-24">
        <div className="text-viz-text-secondary text-lg">Loading...</div>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 pt-20 pb-24">
        <div className="text-viz-error text-lg">Bot not found</div>
        <Link to="/bots" className="text-viz-accent mt-4 inline-block">Back to Bots</Link>
      </div>
    );
  }

  // Show all monitored symbols (scanner already filtered them by score)
  const hasPattern = (d: SymbolChartData) =>
    d.signal ||
    (d.phase && d.phase !== 'idle' && d.phase !== 'watching') ||
    (d.squeeze_score !== undefined && d.squeeze_score > 0) ||
    (d.klines && d.klines.length > 0);

  // Sort: signals first → active phase → squeeze_score → volume_rank → symbol name
  const activeData = chartData.filter(hasPattern);
  const sortedData = [...activeData].sort((a, b) => {
    if (a.signal && !b.signal) return -1;
    if (!a.signal && b.signal) return 1;
    const aActive = a.phase && a.phase !== 'idle' && a.phase !== 'watching' ? 1 : 0;
    const bActive = b.phase && b.phase !== 'idle' && b.phase !== 'watching' ? 1 : 0;
    if (aActive !== bActive) return bActive - aActive;
    const scoreDiff = (b.squeeze_score || 0) - (a.squeeze_score || 0);
    if (scoreDiff !== 0) return scoreDiff;
    const rankDiff = (a.volume_rank || 999) - (b.volume_rank || 999);
    if (rankDiff !== 0) return rankDiff;
    return a.symbol.localeCompare(b.symbol);
  });

  return (
    <div className="max-w-[1400px] mx-auto px-5 pt-20 pb-24">
      {/* Header */}
      <div className="bot-detail-header">
        <div className="bot-detail-back">
          <Link to="/bots" className="text-viz-text-secondary hover:text-viz-text-primary transition-colors">
            &#8592; Bots
          </Link>
        </div>
        <div className="bot-detail-info">
          <h1 className="text-viz-text-primary text-2xl font-bold">
            {bot.scan_mode ? 'Auto Scanner' : bot.symbol}
          </h1>
          <div className="bot-detail-badges">
            <span className="badge badge-exchange">{bot.exchange.toUpperCase()}</span>
            <span className="badge badge-strategy">{getStrategyName(bot.strategy_type)}</span>
            {bot.scan_mode && (
              <span className="badge badge-scanner">
                {sortedData.length > 0
                  ? `${sortedData.length} active / ${chartData.length} scanning`
                  : `Scanning ${chartData.length}`}
              </span>
            )}
            <span className={`badge badge-status ${getStatusClass(bot.status)}`}>
              {bot.status.charAt(0).toUpperCase() + bot.status.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-viz-error-bg border border-viz-error rounded-lg text-viz-error text-sm">
          {error}
        </div>
      )}

      {/* Session Stats Banner */}
      {sessionStats && sessionStats.total_signals > 0 && (
        <div className="session-stats-banner">
          <span>Session: {formatDuration(sessionStats.session_duration_sec)}</span>
          <span>Signals: {sessionStats.total_signals}</span>
          <span>Win: {sessionStats.wins}/{sessionStats.wins + sessionStats.losses} ({sessionStats.win_rate.toFixed(0)}%)</span>
          <span>Avg P&L: <span className={sessionStats.avg_pnl_pct >= 0 ? 'text-green-400' : 'text-red-400'}>
            {sessionStats.avg_pnl_pct >= 0 ? '+' : ''}{sessionStats.avg_pnl_pct.toFixed(1)}%
          </span></span>
          <span>Expired: {sessionStats.expired}</span>
        </div>
      )}

      {/* Strategy Config (collapsible) */}
      {bot.strategy_type === 'volume_breakout_v2' && bot.config && (
        <div className="mb-4">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="text-viz-text-secondary text-xs hover:text-viz-text-primary transition-colors"
          >
            {showConfig ? 'Hide' : 'Show'} Strategy Config
          </button>
          {showConfig && (
            <div className="mt-2 bg-viz-bg-secondary border border-viz-border rounded-lg p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 text-xs">
              {[
                ['RVOL', `${bot.config.volume_spike_x ?? 2.0}x`],
                ['Max Spikes', bot.config.max_prior_spikes ?? 3],
                ['RSI Range', `${bot.config.rsi_min_momentum ?? 50}-${bot.config.rsi_extreme ?? 92}`],
                ['Body Ratio', bot.config.body_ratio_min ?? 0.4],
                ['SL', `${bot.config.sl_atr_multiplier ?? 1.5}x ATR`],
                ['TP', `${bot.config.tp_atr_multiplier ?? 2.25}x ATR`],
                ['ATR Period', bot.config.atr_period ?? 10],
                ['Cooldown', `${bot.config.cooldown_minutes ?? 5}m`],
                ['Squeeze Min', bot.config.min_squeeze_score ?? 30],
                ['Leverage', `${bot.config.leverage ?? 5}x`],
              ].map(([label, val], i) => (
                <div key={i}>
                  <span className="text-viz-text-secondary block">{label}</span>
                  <span className="text-viz-text-primary font-mono">{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Charts Grid */}
      {sortedData.length === 0 ? (
        <div className="bg-viz-bg-secondary border border-viz-border rounded-xl p-12 text-center backdrop-blur-glass shadow-card">
          <div className="text-viz-text-secondary mb-2 text-lg">
            {chartData.length > 0
              ? 'No active patterns'
              : bot.status !== 'running' ? 'Start the bot to see live charts' : 'Waiting for candle data...'}
          </div>
          {chartData.length > 0 && (
            <div className="text-viz-text-secondary text-sm">
              Scanning {chartData.length} symbols — waiting for squeeze setups (score &ge; 50)
            </div>
          )}
        </div>
      ) : (
        <div className="mini-chart-grid">
          {sortedData.map((item) => (
            <MiniChart
              key={item.symbol}
              symbol={item.symbol}
              klines={item.klines}
              signal={item.signal}
              lastPrice={item.last_price}
              onClick={() => setSelectedSymbol(item)}
              phase={item.phase}
              indicators={item.indicators}
              volumeRank={item.volume_rank}
              turnover24h={item.turnover_24h}
              squeezeScore={item.squeeze_score}
              scoreBreakdown={item.score_breakdown}
              fundingRate={item.funding_rate}
            />
          ))}
        </div>
      )}

      {/* Completed Signals Section */}
      {completedSignals.length > 0 && (
        <div className="completed-signals-section">
          <h3 className="text-viz-text-secondary text-sm font-semibold mb-3">Completed Signals</h3>
          <div className="completed-signals-table">
            {completedSignals.slice().reverse().map((sig, idx) => (
              <div key={idx} className={`completed-signal-row result-${sig.result}`}>
                <span className="cs-symbol">{sig.symbol.replace('USDT', '')}</span>
                <span className={`cs-dir ${sig.direction === 'LONG' ? 'dir-long' : 'dir-short'}`}>
                  {sig.direction === 'LONG' ? '↑' : '↓'} {sig.direction}
                </span>
                <span className="cs-entry">${sig.entry_price.toFixed(2)}</span>
                <span className="cs-arrow">→</span>
                <span className="cs-exit">${sig.exit_price.toFixed(2)}</span>
                <span className={`cs-pnl ${sig.virtual_pnl_pct >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                  {sig.virtual_pnl_pct >= 0 ? '+' : ''}{sig.virtual_pnl_pct.toFixed(1)}%
                </span>
                <span className="cs-result">{sig.result === 'tp_hit' ? '✓' : sig.result === 'sl_hit' ? '✗' : '⏱'}</span>
                <span className="cs-time">{Math.round(sig.duration_sec / 60)}m</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart Modal */}
      {selectedSymbol && (
        <ChartModal
          symbol={selectedSymbol.symbol}
          klines={selectedSymbol.klines}
          signal={selectedSymbol.signal}
          lastPrice={selectedSymbol.last_price}
          onClose={() => setSelectedSymbol(null)}
          phase={selectedSymbol.phase}
          indicators={selectedSymbol.indicators}
          volumeRank={selectedSymbol.volume_rank}
          turnover24h={selectedSymbol.turnover_24h}
          squeezeScore={selectedSymbol.squeeze_score}
          scoreBreakdown={selectedSymbol.score_breakdown}
          fundingRate={selectedSymbol.funding_rate}
        />
      )}
    </div>
  );
};

export default BotDetail;
